// A failed mutation has to say so.
//
// W39.65. The mobile UX sweep walks twelve screens on a healthy connection, and
// every control on them is a network call. On mobile data the interesting case
// is a request that never arrives — neither a 4xx nor a 5xx, so none of the
// 68 mapped codes in `appMessages.ts` applies.
//
// Measured with the network aborted, on `/diaries` and `/wishes`:
//
//     --- spoke up on 0, silent on 0 of 5
//     --- spoke up on 0, silent on 0 of 8
//
// Zero controls told the user anything. `DiaryLikeButton` is the clearest case,
// and it is not a missing-message oversight so much as the design working as
// written: `useDiaryLikes` toggles the heart **optimistically** and rolls back
// on error, so on a dead network the heart flips, flips back, and the person is
// left unable to tell whether their tap registered, whether the app is broken,
// or whether they should try again. On a couples app that is "I liked your
// diary" and then nothing.
//
// The fix is one place rather than thirty: TanStack Query's mutation cache knows
// about every failure, including the ones no component subscribed to.
//
// **Why a call site that wrote its own `onError` is left alone.** `useMutation`
// merges `defaultOptions` with the caller's options, so once a default exists
// every mutation *looks* like it has an `onError`. Reference equality against
// this module's exported function is what separates "the call site took
// responsibility for telling the user" from "nobody did", and
// `CalendarEventDetail` is the case that motivated it — it already pushes its
// own `danger` toast, and a second one would be worse than silence.
//
// **How the failure is observed, and why it is not the obvious call.** This was
// written first against a `type: "error"` cache event, which does not exist in
// the installed `@tanstack/query-core@5.103.1` — that build reports lifecycle
// through a single `type: "updated"` event carrying `action.type`, and the
// filter matched nothing, so the reporter was silently dead. The event types are
// now read out of
// `node_modules/.../query-core/build/modern/mutation.js` rather than remembered:
// the only `notify` call sites are `observerAdded`, `observerRemoved` and
// `updated`.
//
// Queries are deliberately not covered. A failed query renders an `ErrorState`
// with a retry button, which is a better surface than a toast; it is the
// mutations that have no such surface.

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/lib/api/pair";
import { useToast } from "@/components/ui/toast";
import { appErrorMessage } from "@/lib/i18n/app";
import { API_REQUEST_FAILED } from "@/lib/i18n/errors";

/**
 * The default `onError` for every mutation. Stable reference on purpose: the
 * reporter below compares against it to decide whether a call site opted out.
 */
// Allow a non-component export alongside `<MutationErrorReporter />` —
// `defaultMutationOnError` is imported by `main.tsx`, and the reference
// comparison below depends on it being the *same* function, so it cannot live
// in a separate module and stay one object. Same reason `toast.tsx` disables
// this rule for `useToast`.
/* eslint-disable react-refresh/only-export-components */
export function defaultMutationOnError(): void {
  // Intentionally does nothing. Its existence is the signal — see the module
  // docstring. Reporting happens in `<MutationErrorReporter />`, which needs
  // React context and so cannot live here.
}

function messageFor(error: unknown): string {
  if (error instanceof ApiError) return appErrorMessage(error.code, error.message);
  // A rejected `fetch` is a `TypeError` with no status and no code — the
  // "no response at all" case that motivated this file.
  return API_REQUEST_FAILED;
}

/**
 * Mount once, inside `<ToastProvider>` and inside the same `QueryClientProvider`
 * the app uses. Reports every mutation failure that no call site reported itself.
 */
export function MutationErrorReporter(): null {
  const queryClient = useQueryClient();
  // `useToast()` returns `{ push, dismiss }` — the context has no `pushToast`
  // member. Destructuring the wrong name left `pushToast` undefined here, and
  // calling it threw *inside* the cache subscriber, where the notify manager
  // swallowed it: the reporter logged that it was reporting and then nothing
  // appeared, which looks exactly like "the reporter does not work". The
  // rename is the same one `CalendarEventDetail` makes.
  const { push: pushToast } = useToast();

  useEffect(() => {
    return queryClient.getMutationCache().subscribe((event) => {
      if (event.type !== "updated" || event.action.type !== "error") return;
      const { mutation } = event;
      // The caller's own handler wins: it knows what the person was trying to
      // do, and a second toast on top of its message is noise, not help.
      if (mutation.options.onError !== defaultMutationOnError) return;
      pushToast({ message: messageFor(mutation.state.error), variant: "danger" });
    });
  }, [queryClient, pushToast]);

  return null;
}
